import { BoxIcon, PlusIcon } from "@xola/icons";
import { Alert, Button, Table } from "@xola/ui-kit";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { AddOn, CatalogItemDisplaySection, CatalogItemObject, CatalogType, Experience, UnitType } from "types";
import { HeaderToolBarActions } from "../../../Common/HeaderToolbarActions";
import { ContentLayout } from "../../../Layout/ContentLayout";
import { Loader } from "../../../Loader";
import { AddOnItem } from "./AddOnItem";
import { EditAddOnModal } from "./EditAddOnModal";
import { ExperienceAddOns } from "./ExperienceAddOns";
import { ExperienceCreateAddOnPopover } from "./ExperienceCreateAddOnPopover";
import { useForm } from "@/hooks/questionnaireFormHooks";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

export const AddOnList = ({ experience }: { experience?: Experience }) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const history = useHistory();
    const params = experience
        ? {
              product: experience.id,
              "unitType[in]": `${UnitType.ADDON},${UnitType.PICKUP}`,
              object: CatalogItemObject.PRODUCT_CATALOG_ITEM,
              "type[nin]": CatalogType.CHOICES_ITEM,
          }
        : {
              "unitType[in]": `${UnitType.ADDON},${UnitType.PICKUP}`,
              object: CatalogItemObject.GLOBAL_CATALOG_ITEM,
              "type[nin]": CatalogType.CHOICES_ITEM,
          };
    const { data: addOns, isLoading } = useAutoFetchCollection<AddOn>("catalogItems", params);
    const { form: questionnaireForm } = useForm(experience?.id);

    // filter out addons with no type or choices_item type (keep only regular addons)
    const filteredAddOns = useMemo(
        () => addOns.filter((addOn) => addOn.type && addOn.type !== CatalogType.CHOICES_ITEM),
        [addOns],
    );

    const handleQuestionnaireSettingsClick = () => {
        if (experience?.id) {
            history.push(`/products/experiences/${experience.id}/questionnaire`);
        }
    };

    const { questionnaireAddons, organizerAddons } = useMemo(() => {
        const questionnaireAddons: AddOn[] = [];
        const organizerAddons: AddOn[] = [];

        for (const addOn of filteredAddOns) {
            if (addOn.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE)) {
                questionnaireAddons.push(addOn);
            } else {
                organizerAddons.push(addOn);
            }
        }

        // sort the questionnaireAddons in the same order as in the questionnaire form
        if (questionnaireForm) {
            questionnaireAddons.sort((a, b) => {
                const aField = questionnaireForm.fields.find((field) => field.catalogItem?.id === a.id);
                const bField = questionnaireForm.fields.find((field) => field.catalogItem?.id === b.id);

                if (!aField && !bField) return 0;
                if (!aField) return 1;
                if (!bField) return -1;

                return (aField.seq ?? 0) - (bField.seq ?? 0);
            });
        }

        return { questionnaireAddons, organizerAddons };
    }, [filteredAddOns, questionnaireForm]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <ContentLayout isWide>
            <HeaderToolBarActions>
                {experience ? (
                    <ExperienceCreateAddOnPopover experience={experience} />
                ) : (
                    <Button color="primary" icon={<PlusIcon />} onClick={() => setShowCreateModal(true)}>
                        Create Add-on
                    </Button>
                )}
            </HeaderToolBarActions>
            <div className="mt-5 space-y-4 px-1.5">
                <div className="text-xl font-bold">Add-ons</div>
                {filteredAddOns.length > 0 ? (
                    experience ? (
                        <div className="rounded-lg border border-gray-lighter">
                            <Table>
                                {organizerAddons.length > 0 && (
                                    <>
                                        <Table.Head data-testid="ask-organizer-addon-list-section">
                                            <Table.Row>
                                                <Table.Header>
                                                    <div className="flex flex-col">
                                                        <span className="text-base font-bold">Ask Organizer</span>
                                                        <span className="text-base font-normal">
                                                            These add-ons are presented in the add-ons section in the
                                                            checkout workflow
                                                        </span>
                                                    </div>
                                                </Table.Header>
                                            </Table.Row>
                                        </Table.Head>
                                        <Table.Body>
                                            <Table.Row>
                                                <Table.Cell>
                                                    <ExperienceAddOns
                                                        addOns={organizerAddons}
                                                        experience={experience}
                                                    />
                                                </Table.Cell>
                                            </Table.Row>
                                        </Table.Body>
                                    </>
                                )}

                                {questionnaireAddons.length > 0 && (
                                    <>
                                        <Table.Head
                                            className="!border-t-0"
                                            data-testid="ask-each-attendee-addon-list-section"
                                        >
                                            <Table.Row>
                                                <Table.Header>
                                                    <div className="flex flex-col">
                                                        <span className="text-base font-bold">Ask Each Attendee</span>
                                                        <span className="text-base font-normal">
                                                            Attendee add-ons appear in your customer&apos;s
                                                            questionnaire. The display order can be defined in{" "}
                                                            <span
                                                                className="cursor-pointer font-bold text-blue"
                                                                onClick={handleQuestionnaireSettingsClick}
                                                            >
                                                                questionnaire settings
                                                            </span>
                                                            .
                                                        </span>
                                                    </div>
                                                </Table.Header>
                                            </Table.Row>
                                        </Table.Head>
                                        <Table.Body>
                                            <Table.Row>
                                                <Table.Cell>
                                                    <div className="h-full flex-1 rounded">
                                                        <div className="divide-y divide-gray-light rounded pr-1">
                                                            {questionnaireAddons.map((addOn: AddOn) => (
                                                                <div
                                                                    key={addOn.id}
                                                                    className="flex items-center space-x-4"
                                                                    data-testid="experience-addons-item"
                                                                >
                                                                    <div className="w-4.5 flex-shrink-0" />
                                                                    <AddOnItem addOn={addOn} experience={experience} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </Table.Cell>
                                            </Table.Row>
                                        </Table.Body>
                                    </>
                                )}
                            </Table>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-light overflow-y-auto overflow-x-hidden rounded pr-1">
                            {filteredAddOns.map((addOn: AddOn) => (
                                <AddOnItem key={addOn.id} addOn={addOn} experience={experience} />
                            ))}
                        </div>
                    )
                ) : (
                    <NoAddOnsAlert text={experience ? "No add-ons found for this experience" : ""} />
                )}
            </div>
            <EditAddOnModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />
        </ContentLayout>
    );
};

const NoAddOnsAlert = ({ text = "" }) => {
    if (!text) {
        text = "You do not have any add-ons yet. Please create your first add-on to get started.";
    }

    return (
        <Alert color="primary">
            <div className="flex p-2">
                <BoxIcon size="large" />
                <div className="ml-2">
                    <div className="mb-3 flex space-x-2">
                        <span className="text-md font-bold" data-testid="addon-list-no-addons-found">
                            No Add-ons Found
                        </span>
                    </div>
                    <span className="text-base">{text}</span>
                </div>
            </div>
        </Alert>
    );
};
